import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import seoData from '../config/seoData';
import EquipmentCard from '../components/EquipmentCard';
import equipments from '../data/equipments';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Phone,
  MessageCircle,
  Instagram,
  Menu,
  X,
  Filter,
  Search,
  CheckCircle,
  ArrowRight,
  Building2,
  Package,
  Zap
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const EquipamentosDisponiveis = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const whatsappNumber = '5531971823249';

  // Scroll para o topo quando a página carregar
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleWhatsAppGeneral = () => {
    const message = encodeURIComponent(
      'Olá, WellTec! Gostaria de orientação para escolher o equipamento ideal para minha operação.'
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  const handleCallClick = () => {
    window.location.href = 'tel:+5531971823249';
  };

  // Filtrar equipamentos
  const filteredEquipments = equipments.filter((equipment) => {
    const matchesCategory =
      selectedCategory === 'all' || equipment.category === selectedCategory;
    const matchesSearch =
      searchTerm === '' ||
      equipment.manufacturer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      equipment.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      equipment.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Contar por categoria
  const lavadorasCount = equipments.filter((e) => e.category === 'lavadora').length;
  const varredeirasCount = equipments.filter((e) => e.category === 'varredeira').length;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={seoData['/equipamentos-disponiveis'].title}
        description={seoData['/equipamentos-disponiveis'].description}
        canonical={seoData['/equipamentos-disponiveis'].canonical}
      />

      {/* Fixed WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-50 animate-bounce-slow">
        <Button
          onClick={handleWhatsAppGeneral}
          className="bg-green-500 hover:bg-green-600 text-white rounded-full w-16 h-16 shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110"
        >
          <MessageCircle className="w-8 h-8" />
        </Button>
      </div>

      {/* Header */}
      <header className="bg-[#253551] text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png"
              alt="WellTec Logo"
              className="h-16 w-auto"
            />
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-[#00B4D8] transition-colors">
              Início
            </Link>
            <Link to="/servicos" className="hover:text-[#00B4D8] transition-colors">
              Serviços
            </Link>
            <Link to="/equipamentos-disponiveis" className="text-[#00B4D8] font-semibold">
              Catálogo
            </Link>
            <Link to="/limpeza-pos-obra" className="hover:text-[#00B4D8] transition-colors">
              Limpeza Pós-Obra
            </Link>
            <Link to="/locacao-lavadora-piso" className="hover:text-[#00B4D8] transition-colors">
              Locação
            </Link>
            <Link to="/blog" className="hover:text-[#00B4D8] transition-colors">
              Blog
            </Link>
            <Button
              onClick={handleCallClick}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#253551]"
            >
              <Phone className="w-4 h-4 mr-2" />
              (31) 97182-3249
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-3">
            <Link
              to="/"
              className="block hover:text-[#00B4D8] transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Início
            </Link>
            <Link
              to="/servicos"
              className="block hover:text-[#00B4D8] transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Serviços
            </Link>
            <Link
              to="/equipamentos-disponiveis"
              className="block text-[#00B4D8] font-semibold"
              onClick={() => setMobileMenuOpen(false)}
            >
              Catálogo
            </Link>
            <Link
              to="/limpeza-pos-obra"
              className="block hover:text-[#00B4D8] transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Limpeza Pós-Obra
            </Link>
            <Link
              to="/locacao-lavadora-piso"
              className="block hover:text-[#00B4D8] transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Locação
            </Link>
            <Link
              to="/blog"
              className="block hover:text-[#00B4D8] transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Blog
            </Link>
            <Button onClick={handleCallClick} variant="outline" className="w-full mt-2">
              <Phone className="w-4 h-4 mr-2" />
              (31) 97182-3249
            </Button>
          </div>
        )}
      </header>

      {/* Seção A - Hero */}
      <section className="relative bg-gradient-to-br from-[#253551] via-[#1e293b] to-[#0f172a] text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Equipamentos para Locação: Lavadoras e Varredeiras de Piso
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-4 font-light">
              Encontre o equipamento ideal para sua operação
            </p>
            <p className="text-lg text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed">
              Conheça as opções de lavadoras e varredeiras de piso da WellTec. Consulte características técnicas, acesse os folhetos dos fabricantes e solicite uma proposta de locação.
            </p>
            <Button
              onClick={() => document.getElementById('catalogo').scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#00B4D8] hover:bg-[#0096c7] text-white px-8 py-6 text-lg font-semibold shadow-xl"
            >
              Ver Catálogo Completo
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      {/* Seção B - Filtros */}
      <section id="catalogo" className="py-12 bg-gray-50 border-y border-gray-200">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            {/* Filtros de Categoria */}
            <div className="flex flex-wrap gap-4 items-center justify-center mb-6">
              <Button
                onClick={() => setSelectedCategory('all')}
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                className={
                  selectedCategory === 'all'
                    ? 'bg-[#253551] text-white'
                    : 'border-[#253551] text-[#253551] hover:bg-[#253551] hover:text-white'
                }
              >
                <Filter className="w-4 h-4 mr-2" />
                Todos ({equipments.length})
              </Button>
              <Button
                onClick={() => setSelectedCategory('lavadora')}
                variant={selectedCategory === 'lavadora' ? 'default' : 'outline'}
                className={
                  selectedCategory === 'lavadora'
                    ? 'bg-[#253551] text-white'
                    : 'border-[#253551] text-[#253551] hover:bg-[#253551] hover:text-white'
                }
              >
                <Package className="w-4 h-4 mr-2" />
                Lavadoras ({lavadorasCount})
              </Button>
              <Button
                onClick={() => setSelectedCategory('varredeira')}
                variant={selectedCategory === 'varredeira' ? 'default' : 'outline'}
                className={
                  selectedCategory === 'varredeira'
                    ? 'bg-[#253551] text-white'
                    : 'border-[#253551] text-[#253551] hover:bg-[#253551] hover:text-white'
                }
              >
                <Zap className="w-4 h-4 mr-2" />
                Varredeiras ({varredeirasCount})
              </Button>
            </div>

            {/* Barra de Pesquisa */}
            <div className="max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar por fabricante, modelo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#00B4D8] transition-colors"
                />
              </div>
            </div>

            {/* Contador de resultados */}
            <p className="text-center mt-4 text-gray-600">
              {filteredEquipments.length === 0
                ? 'Nenhum equipamento encontrado'
                : `${filteredEquipments.length} equipamento${filteredEquipments.length > 1 ? 's' : ''} encontrado${filteredEquipments.length > 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
      </section>

      {/* Seção C - Grid de Equipamentos */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            {filteredEquipments.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredEquipments.map((equipment) => (
                  <EquipmentCard key={equipment.id} equipment={equipment} />
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <p className="text-gray-600 text-lg mb-4">
                  Nenhum equipamento encontrado com os filtros selecionados.
                </p>
                <Button
                  onClick={() => {
                    setSelectedCategory('all');
                    setSearchTerm('');
                  }}
                  variant="outline"
                  className="border-[#253551] text-[#253551]"
                >
                  Limpar Filtros
                </Button>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Seção G - Como Escolher */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#253551] text-center mb-8">
              Como Escolher o Equipamento Ideal
            </h2>
            <p className="text-gray-700 text-center mb-12 leading-relaxed">
              Para recomendar o equipamento mais adequado à sua operação, considere:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {[
                { icon: <Building2 />, text: 'Área total que será limpa' },
                { icon: <Package />, text: 'Tipo de resíduo presente' },
                { icon: <CheckCircle />, text: 'Tipo de piso (concreto, epóxi, cerâmica)' },
                { icon: <Zap />, text: 'Necessidade de varrição ou lavagem' },
                { icon: <ArrowRight />, text: 'Espaço para circulação e manobra' },
                { icon: <CheckCircle />, text: 'Frequência de utilização desejada' }
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 bg-white rounded-lg border-2 border-gray-100"
                >
                  <div className="text-[#00B4D8] flex-shrink-0">{item.icon}</div>
                  <p className="text-gray-700">{item.text}</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <p className="text-gray-700 mb-6">
                Nossa equipe pode ajudar você a identificar o equipamento ideal para sua necessidade.
              </p>
              <Button
                onClick={handleWhatsAppGeneral}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg font-semibold shadow-lg"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Falar com a Equipe WellTec
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Seção H - Atendimento */}
      <section className="py-16 bg-[#253551] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Área de Atendimento</h2>
            <p className="text-lg leading-relaxed mb-8 text-gray-300">
              A WellTec está localizada em <strong>Mário Campos, Minas Gerais</strong>, e atende consultas de locação para <strong>Belo Horizonte e região metropolitana</strong>, conforme disponibilidade e logística.
            </p>
            <p className="text-gray-400 mb-8">
              Entre em contato para verificar a disponibilidade dos equipamentos, condições de locação e prazo de entrega para sua localidade.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={handleWhatsAppGeneral}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp: (31) 97182-3249
              </Button>
              <Button
                onClick={handleCallClick}
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-[#253551]"
              >
                <Phone className="w-5 h-5 mr-2" />
                Ligar Agora
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png"
                alt="WellTec Logo"
                className="h-16 mb-4"
              />
              <p className="text-gray-400 text-sm">
                Locação e manutenção de equipamentos de limpeza profissional.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Links Rápidos</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <Link to="/" className="hover:text-[#00B4D8]">
                    Início
                  </Link>
                </li>
                <li>
                  <Link to="/servicos" className="hover:text-[#00B4D8]">
                    Serviços
                  </Link>
                </li>
                <li>
                  <Link to="/locacao-lavadora-piso" className="hover:text-[#00B4D8]">
                    Locação
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="hover:text-[#00B4D8]">
                    Blog
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Contato</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>(31) 97182-3249</li>
                <li>Mário Campos, MG</li>
                <li>Atendimento: BH e Região</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Redes Sociais</h3>
              <a
                href="https://www.instagram.com/welltec.pisos/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-gray-400 hover:text-[#00B4D8] text-sm"
              >
                <Instagram className="w-5 h-5 mr-2" />
                @welltec.pisos
              </a>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-500">
            <p>&copy; {new Date().getFullYear()} WellTec. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EquipamentosDisponiveis;
